import React, { useEffect, useState } from "react";
import {
    Loader2,
    Plus,
    Pencil,
    Trash2,
    Ticket,
    Percent,
    Euro,
    Truck,
    Save,
    X,
    Copy,
    RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTime, formatPrice } from "../lib/orderStatus";

const TYPE_META = {
    percent: { label: "Percentagem", icon: Percent, color: "text-brand-green", bg: "bg-brand-green-soft" },
    fixed: { label: "Valor fixo", icon: Euro, color: "text-brand-orange", bg: "bg-brand-orange-soft" },
    free_shipping: { label: "Envio grátis", icon: Truck, color: "text-brand-ink", bg: "bg-brand-cream" },
};

const EMPTY = {
    code: "",
    type: "percent",
    value: 10,
    description: "",
    min_subtotal: "",
    max_discount: "",
    usage_limit: "",
    expires_at: "",
    active: true,
};

export default function AdminCoupons({ apiAuth }) {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(true);
    const [editing, setEditing] = useState(null); // null | {} for new | coupon obj for edit

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await apiAuth("/admin/coupons");
            setRows(data || []);
        } catch {
            toast.error("Erro ao carregar cupões");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const remove = async (id) => {
        if (!confirm("Apagar este cupão?")) return;
        try {
            await apiAuth(`/admin/coupons/${id}`, { method: "DELETE" });
            setRows((prev) => prev.filter((c) => c.id !== id));
            toast.success("Cupão apagado");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro ao apagar");
        }
    };

    const copyCode = (code) => {
        navigator.clipboard?.writeText(code);
        toast.success(`Código ${code} copiado`);
    };

    return (
        <div className="space-y-6" data-testid="admin-coupons-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-widest font-bold text-brand-ink-soft">
                    Cupões promocionais ({rows.length})
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={load}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors"
                        data-testid="coupons-refresh"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                        Atualizar
                    </button>
                    <button
                        type="button"
                        onClick={() => setEditing({ ...EMPTY })}
                        data-testid="coupons-new"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
                    >
                        <Plus className="h-3.5 w-3.5" /> Novo cupão
                    </button>
                </div>
            </div>

            <section className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 overflow-hidden">
                {busy ? (
                    <div className="p-16 flex justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-16 text-center">
                        <Ticket className="h-10 w-10 text-brand-green/40 mx-auto" />
                        <p className="mt-4 text-sm text-brand-ink-soft">
                            Ainda não há cupões criados.
                        </p>
                        <button
                            type="button"
                            onClick={() => setEditing({ ...EMPTY })}
                            className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-4 py-2 text-xs font-bold uppercase tracking-widest"
                        >
                            <Plus className="h-3.5 w-3.5" /> Criar o primeiro cupão
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-brand-cream/60 text-[11px] uppercase tracking-widest text-brand-ink-soft">
                                <tr>
                                    <th className="text-left px-6 py-3 font-bold">Código</th>
                                    <th className="text-left px-6 py-3 font-bold">Tipo</th>
                                    <th className="text-right px-6 py-3 font-bold">Desconto</th>
                                    <th className="text-right px-6 py-3 font-bold hidden md:table-cell">Utilizações</th>
                                    <th className="text-left px-6 py-3 font-bold hidden lg:table-cell">Validade</th>
                                    <th className="text-center px-6 py-3 font-bold">Estado</th>
                                    <th className="text-right px-6 py-3 font-bold">Acções</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-brand-green/10">
                                {rows.map((c) => {
                                    const meta = TYPE_META[c.type] || TYPE_META.percent;
                                    const Icon = meta.icon;
                                    return (
                                        <tr
                                            key={c.id}
                                            data-testid={`coupon-row-${c.id}`}
                                            className="hover:bg-brand-cream/40"
                                        >
                                            <td className="px-6 py-3">
                                                <div className="flex items-center gap-2">
                                                    <code className="font-mono font-bold text-brand-ink">
                                                        {c.code}
                                                    </code>
                                                    <button
                                                        type="button"
                                                        onClick={() => copyCode(c.code)}
                                                        className="p-1 rounded-full hover:bg-brand-cream text-brand-ink-soft"
                                                        title="Copiar código"
                                                        data-testid={`coupon-copy-${c.id}`}
                                                    >
                                                        <Copy className="h-3 w-3" />
                                                    </button>
                                                </div>
                                                {c.description && (
                                                    <div className="text-xs text-brand-ink-soft mt-0.5 truncate max-w-[240px]">
                                                        {c.description}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-3">
                                                <span
                                                    className={`inline-flex items-center gap-1 rounded-full ${meta.bg} ${meta.color} px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold`}
                                                >
                                                    <Icon className="h-3 w-3" /> {meta.label}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 text-right font-bold text-brand-ink">
                                                {c.type === "percent" && `${c.value}%`}
                                                {c.type === "fixed" && formatPrice(c.value)}
                                                {c.type === "free_shipping" && "—"}
                                            </td>
                                            <td className="px-6 py-3 text-right text-xs text-brand-ink-soft hidden md:table-cell">
                                                {c.usage_count || 0}
                                                {c.usage_limit ? `/${c.usage_limit}` : ""}
                                            </td>
                                            <td className="px-6 py-3 text-xs text-brand-ink-soft hidden lg:table-cell">
                                                {c.expires_at ? formatDateTime(c.expires_at) : "Sem validade"}
                                            </td>
                                            <td className="px-6 py-3 text-center">
                                                <span
                                                    className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${
                                                        c.active
                                                            ? "bg-brand-green text-white"
                                                            : "bg-gray-200 text-gray-600"
                                                    }`}
                                                >
                                                    {c.active ? "Ativo" : "Inativo"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditing(c)}
                                                        data-testid={`coupon-edit-${c.id}`}
                                                        className="p-2 rounded-full hover:bg-brand-cream text-brand-ink"
                                                        title="Editar"
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => remove(c.id)}
                                                        data-testid={`coupon-delete-${c.id}`}
                                                        className="p-2 rounded-full hover:bg-red-50 text-red-500"
                                                        title="Apagar"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {editing !== null && (
                <CouponModal
                    apiAuth={apiAuth}
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={(saved) => {
                        setRows((prev) => {
                            const idx = prev.findIndex((c) => c.id === saved.id);
                            if (idx >= 0) {
                                const clone = [...prev];
                                clone[idx] = saved;
                                return clone;
                            }
                            return [saved, ...prev];
                        });
                        setEditing(null);
                    }}
                />
            )}
        </div>
    );
}

const CouponModal = ({ apiAuth, initial, onClose, onSaved }) => {
    const isNew = !initial?.id;
    const [form, setForm] = useState({
        code: initial?.code || "",
        type: initial?.type || "percent",
        value: initial?.value ?? 10,
        description: initial?.description || "",
        min_subtotal: initial?.min_subtotal ?? "",
        max_discount: initial?.max_discount ?? "",
        usage_limit: initial?.usage_limit ?? "",
        expires_at: (initial?.expires_at || "").slice(0, 10),
        active: initial?.active ?? true,
    });
    const [saving, setSaving] = useState(false);

    const set = (k) => (e) => {
        const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e;
        setForm((p) => ({ ...p, [k]: v }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                code: form.code.trim(),
                type: form.type,
                value: form.type === "free_shipping" ? 0 : Number(form.value),
                description: form.description || null,
                min_subtotal: form.min_subtotal === "" ? null : Number(form.min_subtotal),
                max_discount:
                    form.type === "percent" && form.max_discount !== ""
                        ? Number(form.max_discount)
                        : null,
                usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit),
                expires_at: form.expires_at || "",
                active: !!form.active,
            };
            const { data } = isNew
                ? await apiAuth("/admin/coupons", { method: "POST", data: payload })
                : await apiAuth(`/admin/coupons/${initial.id}`, {
                      method: "PUT",
                      data: payload,
                  });
            toast.success(isNew ? "Cupão criado" : "Cupão atualizado");
            onSaved(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro a guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="coupon-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
                <div className="px-6 py-4 border-b border-brand-green/10 flex items-center justify-between">
                    <h3 className="font-display font-medium text-lg text-brand-ink">
                        {isNew ? "Novo cupão" : `Editar · ${initial.code}`}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-brand-cream"
                        data-testid="coupon-modal-close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Código</Label>
                            <input
                                value={form.code}
                                onChange={set("code")}
                                placeholder="WELCOME10"
                                data-testid="coupon-input-code"
                                className={inp + " font-mono uppercase"}
                                style={{ textTransform: "uppercase" }}
                            />
                        </div>
                        <div>
                            <Label>Tipo</Label>
                            <select
                                value={form.type}
                                onChange={set("type")}
                                data-testid="coupon-input-type"
                                className={inp}
                            >
                                <option value="percent">Percentagem</option>
                                <option value="fixed">Valor fixo (€)</option>
                                <option value="free_shipping">Envio grátis</option>
                            </select>
                        </div>
                    </div>

                    {form.type !== "free_shipping" && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>
                                    {form.type === "percent" ? "Percentagem (%)" : "Valor (€)"}
                                </Label>
                                <input
                                    type="number"
                                    step={form.type === "percent" ? 1 : 0.5}
                                    min={0}
                                    max={form.type === "percent" ? 100 : 1000}
                                    value={form.value}
                                    onChange={set("value")}
                                    data-testid="coupon-input-value"
                                    className={inp}
                                />
                            </div>
                            {form.type === "percent" && (
                                <div>
                                    <Label>Desconto máximo (€, opcional)</Label>
                                    <input
                                        type="number"
                                        step={0.5}
                                        min={0}
                                        value={form.max_discount}
                                        onChange={set("max_discount")}
                                        placeholder="Sem limite"
                                        data-testid="coupon-input-max-discount"
                                        className={inp}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    <div>
                        <Label>Descrição (opcional)</Label>
                        <input
                            value={form.description}
                            onChange={set("description")}
                            placeholder="Ex.: 10% para novos clientes"
                            data-testid="coupon-input-description"
                            className={inp}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Subtotal mínimo (€, opcional)</Label>
                            <input
                                type="number"
                                step={0.5}
                                min={0}
                                value={form.min_subtotal}
                                onChange={set("min_subtotal")}
                                placeholder="Sem mínimo"
                                data-testid="coupon-input-min-subtotal"
                                className={inp}
                            />
                        </div>
                        <div>
                            <Label>Limite de utilizações (opcional)</Label>
                            <input
                                type="number"
                                step={1}
                                min={1}
                                value={form.usage_limit}
                                onChange={set("usage_limit")}
                                placeholder="Ilimitado"
                                data-testid="coupon-input-usage-limit"
                                className={inp}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Validade (opcional)</Label>
                            <input
                                type="date"
                                value={form.expires_at}
                                onChange={set("expires_at")}
                                data-testid="coupon-input-expires"
                                className={inp}
                            />
                        </div>
                        <div className="flex items-end pb-1">
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={!!form.active}
                                    onChange={set("active")}
                                    data-testid="coupon-input-active"
                                    className="h-4 w-4 rounded accent-brand-green"
                                />
                                <span className="text-sm font-semibold text-brand-ink">
                                    Ativo
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-brand-green/10 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-4 py-2 text-xs font-bold uppercase tracking-widest"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || !form.code}
                        data-testid="coupon-modal-save"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-4 py-2 text-xs font-bold uppercase tracking-widest disabled:opacity-60"
                    >
                        <Save className="h-3.5 w-3.5" />
                        {saving ? "A guardar..." : "Guardar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const Label = ({ children }) => (
    <label className="block text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft mb-1">
        {children}
    </label>
);

const inp =
    "w-full rounded-xl border-2 border-brand-green/15 focus:border-brand-green bg-white px-3 py-2 text-sm text-brand-ink focus:outline-none";
